import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { postTerm, patchTerm } from "@/api/terms";
import { getSkills } from "@/api/skills";

export default function TermForm({ term = null, onSave }) {
    const [form, setForm] = useState({
        name: "",
        term_type: "text", // default to text
        copy: "",
        skill_id: "",
    });

    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [skills, setSkills] = useState([]);

    useEffect(() => {
        // Load skills
        const loadSkills = async () => {
            const skillsData = await getSkills();
            if (skillsData) {
                setSkills(skillsData);
                // No default skill selection - allow null for 'default' terms
            }
        };
        loadSkills();

        if (term) {
            setForm({
                name: term.name || "",
                term_type: term.term_type || "text",
                copy: term.copy || "",
                skill_id: term.skillId || "",
            });
        }
    }, [term]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        const formData = new FormData();
        formData.append("name", form.name);
        formData.append("term_type", form.term_type); // ✅ symbolic, not numeric
        if (form.skill_id) {
            formData.append("skill_id", form.skill_id);
        }

        if (form.term_type === "text") {
            formData.append("copy", form.copy);
        }

        if (file) {
            formData.append("file", file);
        }

        const result = term?.id
            ? await patchTerm(term.id, formData)
            : await postTerm(formData);

        if (result) {
            onSave?.(result);
        } else {
            setError("Something went wrong.");
        }

        setLoading(false);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
                <div className="text-red-600 text-sm bg-red-50 p-2 rounded">{error}</div>
            )}

            <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <Input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700">Skill (optional - leave blank for default terms)</label>
                <select
                    name="skill_id"
                    value={form.skill_id}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                    <option value="">Default (all candidates)</option>
                    {skills.map((skill) => (
                        <option key={skill.id} value={skill.id}>
                            {skill.name}
                        </option>
                    ))}
                </select>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700">Type</label>
                <select
                    name="term_type"
                    value={form.term_type}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                    <option value="text">Text</option>
                    <option value="file">File</option>
                </select>
            </div>

            {form.term_type === "text" && (
                <div>
                    <label className="block text-sm font-medium text-gray-700">Copy</label>
                    <Textarea
                        name="copy"
                        value={form.copy}
                        onChange={handleChange}
                        rows={5}
                    />
                </div>
            )}

            {form.term_type === "file" && (
                <div>
                    <label className="block text-sm font-medium text-gray-700">Upload File</label>
                    <Input type="file" onChange={handleFileChange} />
                </div>
            )}

            <div className="pt-2">
                <Button type="submit" disabled={loading}>
                    {loading ? "Saving..." : term ? "Update Term" : "Create Term"}
                </Button>
            </div>
        </form>
    );
}
